// components/CategoryPositionManager.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
  RestaurantMenu as MenuIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../services/api';

const CategoryPositionManager = ({ open, onClose, language, onSuccess }) => {
  const [allCategories, setAllCategories] = useState([]);
  const [displayCategories, setDisplayCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isDragging, setIsDragging] = useState(false);

  // Sort categories by position
  const sortByPosition = (categories) => {
    return [...categories].sort((a, b) => {
      // Categories with position 0 go to the end
      if (a.category_pos === 0 && b.category_pos !== 0) return 1;
      if (a.category_pos !== 0 && b.category_pos === 0) return -1;
      // Sort by position number
      return a.category_pos - b.category_pos;
    });
  };

  // Fetch all categories
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/categories/all`);
      let categoriesData = response.data.categories || response.data || [];
      
      // CRITICAL: Sort by category_pos AFTER fetching
      const sorted = sortByPosition(categoriesData);
      
      setAllCategories(sorted);
      setDisplayCategories(sorted);
      
      // Check for conflicts (duplicate positions)
      const positionMap = new Map();
      const conflictsList = [];
      sorted.forEach(category => {
        if (category.category_pos !== 0) {
          if (positionMap.has(category.category_pos)) {
            conflictsList.push({
              position: category.category_pos,
              categories: [positionMap.get(category.category_pos), category]
            });
          }
          positionMap.set(category.category_pos, category);
        }
      });
      setConflicts(conflictsList);
      
    } catch (error) {
      console.error('Error fetching categories:', error);
      setSnackbar({
        open: true,
        message: language === 'ar' ? 'فشل في تحميل الأقسام' : 'Failed to load categories',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open]);

  // Apply filters to displayCategories
  const applyFilters = useCallback(() => {
    let filtered = [...allCategories];
    
    if (searchTerm) {
      filtered = filtered.filter(cat => 
        (language === 'ar' ? cat.name : cat.name_en).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setDisplayCategories(filtered);
  }, [allCategories, searchTerm, language]);

  // Run filters when dependencies change
  useEffect(() => {
    if (!isDragging) {
      applyFilters();
    }
  }, [searchTerm, allCategories, applyFilters, isDragging]);

  // Handle drag end
  const handleDragEnd = (result) => {
    setIsDragging(false);
    
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    
    // Create a copy of the current display categories
    const reordered = [...displayCategories];
    
    // Remove the dragged item
    const [draggedItem] = reordered.splice(result.source.index, 1);
    
    // Insert at new position
    reordered.splice(result.destination.index, 0, draggedItem);
    
    // Update positions based on new order (start from 1)
    const updatedDisplay = reordered.map((item, idx) => ({
      ...item,
      category_pos: idx + 1
    }));
    
    // Update the master allCategories array by merging changes
    const updatedAll = allCategories.map(category => {
      const found = updatedDisplay.find(u => u.id === category.id);
      return found || category;
    });
    
    // Sort the updated allCategories by position
    const sortedAll = sortByPosition(updatedAll);
    
    // Update both states
    setDisplayCategories(updatedDisplay);
    setAllCategories(sortedAll);
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send categories that have non-zero positions or have changed
      const updates = allCategories
        .filter(category => category.category_pos !== 0)
        .map(category => ({
          id: category.id,
          category_pos: category.category_pos
        }));
      
      await api.post('/admin/categories/update-all-positions', {
        categories: updates
      });
      
      setSnackbar({
        open: true,
        message: language === 'ar' ? 'تم تحديث ترتيب الأقسام بنجاح' : 'Category positions updated successfully',
        severity: 'success'
      });
      
      onSuccess?.();
      
      // Refresh the list after save to ensure correct order
      await fetchCategories();
      
    } catch (error) {
      console.error('Error saving positions:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || (language === 'ar' ? 'فشل في حفظ الترتيب' : 'Failed to save positions'),
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchCategories();
  };

  const handleFixConflicts = () => {
    const fixedCategories = [...allCategories];
    const usedPositions = new Set();
    
    fixedCategories.forEach((category, index) => {
      let newPos = index + 1;
      while (usedPositions.has(newPos)) {
        newPos++;
      }
      category.category_pos = newPos;
      usedPositions.add(newPos);
    });
    
    const sortedFixed = sortByPosition(fixedCategories);
    setAllCategories(sortedFixed);
    applyFilters();
    setConflicts([]);
    
    setSnackbar({
      open: true,
      message: language === 'ar' ? 'تم إصلاح التعارضات تلقائياً' : 'Conflicts fixed automatically',
      severity: 'info'
    });
  };

  // Helper function to get item style
  const getItemStyle = (isDragging, draggableStyle) => ({
    userSelect: 'none',
    marginBottom: '8px',
    backgroundColor: isDragging ? '#1A1A1A' : '#0D0D0D',
    border: '1px solid #2D2D2D',
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    ...draggableStyle,
    cursor: 'grab'
  });

  const getListStyle = (isDraggingOver) => ({
    background: isDraggingOver ? 'rgba(255, 215, 0, 0.05)' : 'transparent',
    padding: '8px',
    borderRadius: '12px',
    minHeight: '400px',
    maxHeight: '500px',
    overflowY: 'auto'
  });

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            borderRadius: '20px',
            border: '1px solid #FFD700',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #2D2D2D',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box>
            <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 700 }}>
              {language === 'ar' ? 'ترتيب الأقسام' : 'Section Order'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
              {language === 'ar' 
                ? `إجمالي ${allCategories.length} قسم - اسحب وأفلت لإعادة الترتيب`
                : `${allCategories.length} total sections - Drag and drop to reorder`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleReset}
              size="small"
              sx={{ color: '#B3B3B3' }}
            >
              {language === 'ar' ? 'تحديث' : 'Refresh'}
            </Button>
            <IconButton onClick={onClose} sx={{ color: '#B3B3B3' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Search Bar */}
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid #2D2D2D',
          }}>
            <TextField
              fullWidth
              size="small"
              placeholder={language === 'ar' ? 'بحث عن قسم...' : 'Search section...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#B3B3B3' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1A1A1A',
                  '& fieldset': { borderColor: '#2D2D2D' },
                  '&:hover fieldset': { borderColor: '#FFD700' },
                },
                '& input': { color: '#FFFFFF' }
              }}
            />
          </Box>

          {conflicts.length > 0 && (
            <Alert 
              severity="warning"
              icon={<WarningIcon />}
              sx={{ 
                m: 2, 
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                color: '#FF9800',
                border: '1px solid #FF9800',
              }}
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={handleFixConflicts}
                  sx={{ color: '#FF9800' }}
                >
                  {language === 'ar' ? 'إصلاح تلقائي' : 'Auto Fix'}
                </Button>
              }
            >
              {language === 'ar' 
                ? `تم اكتشاف ${conflicts.length} تعارض في أرقام الترتيب`
                : `Found ${conflicts.length} position conflicts`
              }
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#FFD700' }} />
            </Box>
          ) : displayCategories.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? 'لا توجد أقسام' : 'No sections found'}
              </Typography>
            </Box>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
              <Droppable droppableId="droppable">
                {(provided, snapshot) => (
                  <Box
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={getListStyle(snapshot.isDraggingOver)}
                  >
                    {displayCategories.map((category, index) => (
                      <Draggable
                        key={category.id.toString()}
                        draggableId={category.id.toString()}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getItemStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style
                            )}
                          >
                            <Paper sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                              {/* Drag Handle */}
                              <DragIcon sx={{ color: '#FFD700', cursor: 'grab' }} />
                              
                              {/* Position Number */}
                              <Box sx={{
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#1A1A1A',
                                borderRadius: '8px',
                              }}>
                                <Typography sx={{ color: '#FFD700', fontWeight: 700, fontSize: '1.1rem' }}>
                                  {index + 1}
                                </Typography>
                              </Box>

                              {/* Category Icon/Avatar */}
                              <Avatar 
                                src={category.ico} 
                                sx={{ 
                                  width: 48, 
                                  height: 48, 
                                  backgroundColor: '#1A1A1A',
                                  border: '1px solid #2D2D2D'
                                }}
                              >
                                {!category.ico && <CategoryIcon sx={{ color: '#FFD700' }} />}
                              </Avatar>

                              {/* Category Info */}
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                                  {language === 'ar' ? category.name : category.name_en}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                                  {category.description && (
                                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                                      {language === 'ar' ? category.description : category.description_en}
                                    </Typography>
                                  )}
                                  {category.card_limit > 0 && (
                                    <Chip
                                      label={`${language === 'ar' ? 'حد' : 'Limit'}: ${category.card_limit}`}
                                      size="small"
                                      sx={{
                                        backgroundColor: '#1A1A1A',
                                        color: '#B3B3B3',
                                        fontSize: '0.65rem',
                                        height: 20,
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>

                              {/* POS Position Badge */}
                              <Chip
                                icon={<MenuIcon sx={{ fontSize: 14 }} />}
                                label={`POS: ${category.category_pos === 0 ? 'Auto' : category.category_pos}`}
                                size="small"
                                sx={{
                                  backgroundColor: category.category_pos === 0 ? '#FF9800' : '#1A1A1A',
                                  color: category.category_pos === 0 ? '#000000' : '#FFD700',
                                  fontSize: '0.7rem',
                                  height: 28,
                                  fontWeight: 600,
                                  '& .MuiChip-icon': {
                                    color: category.category_pos === 0 ? '#000000' : '#FFD700',
                                  }
                                }}
                              />
                            </Paper>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          p: 2, 
          borderTop: '1px solid #2D2D2D',
          gap: 1,
          justifyContent: 'space-between'
        }}>
          <Typography variant="caption" sx={{ color: '#6B6B6B' }}>
            {language === 'ar' 
              ? 'اسحب الأقسام لإعادة ترتيبها. الموضع 0 = ظهور عشوائي'
              : 'Drag sections to reorder. Position 0 = random display'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={onClose}
              sx={{ color: '#B3B3B3' }}
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || loading}
              sx={{
                backgroundColor: '#FFD700',
                color: '#000000',
                fontWeight: 700,
                '&:hover': { backgroundColor: '#FFE55C' }
              }}
            >
              {saving ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') : (language === 'ar' ? 'حفظ الترتيب' : 'Save Order')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CategoryPositionManager;